// Realtime refresh (U13) — graceful no-op unless a broadcaster is configured.
//
// The legacy Vue admin listens on public channels `sale-event` (.SaleEvent),
// `register-event` (.RegisterEvent) and `register-record-event`
// (.RegisterRecordEvent) through `window.Echo`. On this stack the backend runs
// BROADCAST_DRIVER=redis with no Echo server wired (the legacy `new Echo(...)`
// is commented out), so nothing is ever delivered and the legacy dashboard does
// not refresh either.
//
// Flag: realtime is enabled only when a host page provides a ready
// `window.Echo` instance (laravel-echo + pusher-js/socket.io pointed at the
// broadcaster, e.g. the `vendia-soketi` container once BROADCAST_DRIVER=pusher).
// `VITE_VENDIA_REALTIME=off` disables it explicitly. Consumers subscribe with
// `onRealtime()` and also receive a DOM event `vendia:realtime` so pages owned
// by other features can refresh without importing this module.

type EchoChannel = { listen: (event: string, cb: (payload: unknown) => void) => EchoChannel; stopListening?: (event: string) => EchoChannel };
type EchoLike = { channel: (name: string) => EchoChannel; leave?: (name: string) => void };

export type RealtimeTopic = 'sale' | 'register' | 'register-record';

const CHANNELS: Array<{ topic: RealtimeTopic; channel: string; event: string }> = [
  { topic: 'sale', channel: 'sale-event', event: '.SaleEvent' },
  { topic: 'register', channel: 'register-event', event: '.RegisterEvent' },
  { topic: 'register-record', channel: 'register-record-event', event: '.RegisterRecordEvent' }
];

let started = false;

function echo(): EchoLike | null {
  if (import.meta.env.VITE_VENDIA_REALTIME === 'off') return null;
  const candidate = (window as unknown as { Echo?: EchoLike }).Echo;
  return candidate && typeof candidate.channel === 'function' ? candidate : null;
}

export function isRealtimeEnabled() {
  return echo() !== null;
}

/** Subscribe the shell to the legacy broadcast channels (idempotent). */
export function startRealtime() {
  const instance = echo();
  if (!instance || started) return;
  started = true;
  for (const { topic, channel, event } of CHANNELS) {
    try {
      instance.channel(channel).listen(event, (payload) => {
        window.dispatchEvent(new CustomEvent('vendia:realtime', { detail: { topic, payload } }));
      });
    } catch {
      /* broadcaster unavailable — stay silent */
    }
  }
}

/** Run `fn` whenever one of `topics` is broadcast. Returns an unsubscribe function. */
export function onRealtime(topics: RealtimeTopic[], fn: (topic: RealtimeTopic, payload: unknown) => void) {
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ topic: RealtimeTopic; payload: unknown }>).detail;
    if (detail && topics.includes(detail.topic)) fn(detail.topic, detail.payload);
  };
  window.addEventListener('vendia:realtime', handler);
  return () => window.removeEventListener('vendia:realtime', handler);
}
